import React, { useState } from 'react';
import Title from '../ContentCard/Title';
import PropTypes from 'prop-types';
import ContentType, { typeDisplayMap } from '../ContentCard/ContentType';
import styles from './NewContentCard.module.less';
import Dicon from '../Dicon';
import clsx from 'clsx';
import { Remarkable } from 'remarkable';
import { Image } from 'edcom-ui';

const NewContentCard = props => {
  const {
    type,
    grade = '',
    category = '',
    thumbnail,
    isPremium = false,
    isEL = false,
    isNew = false,
    isAdminOnly = false,
    withinGrid = false,
    className,
    title,
    description,
    isInteractive = false,
    children,
    ...otherProps
  } = props;

  const md = new Remarkable({ html: true });

  const defaultedDescription = description === null ? '' : description;

  // Exercises (aka skill builders) are a weird shape and need to be scaled, so a higher resolution is required
  const imageWidth = type === 'skill-builder' ? 320 : 230;
  let whiteBackground;

  const [hoverOpen, setHoverOpen] = useState(false);

  let hoverOpenTimeoutID = null;

  // Catch the situation when the image fails to load/is broken
  const [imageError, setImageError] = useState(false);

  let onImageError = () => {
    console.error('Image fails to load: ', thumbnail.src);
    setImageError(true);
  };

  const clearHoverOpenTimeout = () => {
    if (hoverOpenTimeoutID) {
      clearTimeout(hoverOpenTimeoutID);
      hoverOpenTimeoutID = null;
    }
  };

  let rating,
    image,
    largeImage,
    checkbox,
    collectible,
    largeImageHeight,
    largeImageWidth;
  React.Children.forEach(children, child => {
    if (!child) return; // ignore null
    if (!child.type || !child.type.displayName) {
      console.error('Unexpected child', child);
      return;
    }

    switch (child.type.displayName) {
      case 'StarRating':
        rating = <div className={styles.ratingHolder}>{child}</div>;
        break;
      case 'ContentCardCheckbox':
        checkbox = child;
        break;
      case 'ContentCardCollectible':
        collectible = child;
        break;
      default:
        console.error('Unexpected child: ', child.type.displayName);
    }
  });

  if (
    !imageError &&
    thumbnail &&
    thumbnail.src &&
    thumbnail.width &&
    thumbnail.height
  ) {
    image = (
      <Image
        src={thumbnail.src}
        alt={title}
        displayWidth={imageWidth}
        // This is a hack to force the image to scale via the CDN
        naturalWidth={imageWidth + 1}
        onError={onImageError}
      />
    );

    // In hover state, image is scaled to width = 300px while keeping aspect ratio
    largeImageWidth = 300;
    largeImageHeight = (300 / thumbnail.width) * thumbnail.height;

    // Pad image with white background to height = 200px if scaled height is < 200px
    if (largeImageHeight < 200) whiteBackground = true;

    largeImage = (
      <Image
        src={thumbnail.src}
        alt={title + ' large image'}
        height={largeImageHeight}
        width={largeImageWidth}
      />
    );
  } else {
    // Fall back to using the content type icon
    image = <Dicon name={type} />;
    largeImage = <Dicon name={type} size="largeImage" />;
  }

  return (
    <div
      className={clsx(styles[className], styles.card, {
        [styles.grid]: withinGrid,
        [styles.newContent]: isNew,
        [styles.adminOnly]: isAdminOnly,
        [styles.hoverOpen]: hoverOpen,
      })}
      {...otherProps}
      onMouseEnter={() => {
        clearHoverOpenTimeout();
        hoverOpenTimeoutID = setTimeout(() => {
          setHoverOpen(true);
        }, 500);
      }}
      onMouseLeave={() => {
        clearHoverOpenTimeout();
        setHoverOpen(false);
      }}
    >
      <div className={styles.container}>
        <div className={styles.front}>
          <div className={styles.image}>{image}</div>
          <div className={styles.titleArea}>
            <Title withinGrid={withinGrid}>{title}</Title>
            <ContentType
              isPremium={isPremium}
              isEL={isEL}
              isInteractive={isInteractive}
              type={type}
              withinGrid={withinGrid}
            />
            {rating}
          </div>
        </div>
      </div>
      <div className={styles.hoverSection}>
        <div
          className={clsx(
            styles.largeImage,
            whiteBackground && styles.whiteBackground
          )}
        >
          {largeImage}
        </div>
        <div className={clsx(styles.textSection)}>
          <div className={clsx('font-primary-bold', styles.title)}>
            <span>{title}</span>
          </div>
          <div className={styles.categories}>
            {grade && <div>{grade}</div>}
            {category && <div>{category}</div>}
          </div>
          <div
            className={styles.description}
            dangerouslySetInnerHTML={{
              __html: md.renderInline(defaultedDescription),
            }}
          />

          <ContentType
            isPremium={isPremium}
            isEL={isEL}
            isInteractive={isInteractive}
            type={type}
            withinGrid={withinGrid}
            inNewHoverSection={true}
          />
          {rating}
        </div>
      </div>
      {checkbox}
      {collectible}
    </div>
  );
};
NewContentCard.displayName = 'NewContentCard';
NewContentCard.propTypes = {
  type: PropTypes.oneOf(Object.keys(typeDisplayMap)).isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  /** Grade display name (e.g. "1st Grade") */
  grade: PropTypes.string,
  /** Category display name (e.g. "Addition"). Its value is subject name (e.g. "Reading & Writing") for old taxonomy. */
  category: PropTypes.string,
  /** Thumbnail img src */
  thumbnail: PropTypes.object,
  /** Set to true if only premium users are allowed to use this content */
  isPremium: PropTypes.bool,
  isNew: PropTypes.bool,
  isAdminOnly: PropTypes.bool,
  withinGrid: PropTypes.bool,
  isEL: PropTypes.bool,
  /** Set to true if this is a published active interactive worksheet and display animated sparkle on content card */
  isInteractive: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
  collections: PropTypes.object, // change this to array of collection objects instead
};

export default NewContentCard;
